'use client';

import { FC, useEffect, useState } from 'react';
import { useLocale } from '@/react/hooks/useLocale';
import ConfigurationService from '@/services/isomorphic/ConfigurationService';
import ScriptLoader from '../../utility/ScriptLoader';

/**
 * Attentive Marketing sign-up script component.
 * - Only loads in production environment
 * - Waits for user interaction before loading (to avoid interfering with automation tests).
 *
 * @see https://www.attentivemobile.com/
 */
const AttentiveMarketing: FC = () => {
  const locale = useLocale();
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  const attentiveConfig = ConfigurationService.getConfig(
    'attentive',
    locale.language,
    locale.country
  );
  const isAttentiveEnabled = attentiveConfig.getSetting('enabled').value;
  const scriptURL = attentiveConfig.getSetting('scriptURL').value;
  const fallbackScriptURL =
    attentiveConfig.getSetting('fallbackScriptURL').value;

  useEffect(
    /**
     * This effect is necessary to setup the user interaction listener for the
     * Attentive Marketing script.
     * @returns A cleanup function that can be used to cancel the effect.
     */
    function setupUserInteractionListener() {
      // Only proceed if enabled
      if (!isAttentiveEnabled) {
        return;
      }

      // Listen for various user interaction events
      const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];

      // Track user interaction to delay script loading
      const handleUserInteraction = (): void => {
        setHasUserInteracted(true);
        // After the interaction was triggered by one of the events, eagerly cleanup the registered events since
        // they won't be necessary anymore
        events.forEach((event) => {
          window.removeEventListener(event, handleUserInteraction);
        });
      };

      events.forEach((event) => {
        window.addEventListener(event, handleUserInteraction, { once: true });
      });

      // Cleanup event listeners
      // eslint-disable-next-line consistent-return -- We need to return a function to handle cleanup.
      return () => {
        events.forEach((event) => {
          window.removeEventListener(event, handleUserInteraction);
        });
      };
    },
    [
      // Runs again after the boolean changed, since it will only change once, to cleanup the events
      hasUserInteracted,
      isAttentiveEnabled
    ]
  );

  // Fallback script loader function
  const handleScriptError = (): void => {
    const fallbackScript = document.createElement('script');
    fallbackScript.src = fallbackScriptURL;
    fallbackScript.async = true;
    fallbackScript.type = 'text/javascript';
    document.head.appendChild(fallbackScript);
  };

  // Don't render script if disabled, or user hasn't interacted
  if (!isAttentiveEnabled || !hasUserInteracted) {
    return null;
  }

  return (
    <ScriptLoader
      method="next"
      id="attentive-marketing"
      src={scriptURL}
      strategy="afterInteractive"
      async
      onError={handleScriptError}
    />
  );
};

export default AttentiveMarketing;
